import { ReactElement, useState, useMemo } from 'react';
import { Typography, MultiSelect, SearchField, VenueIcon } from '@weareopenr/component-library';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import type { IPublishVenue, IPublishViewProps } from '../types';
import styles from './PublishView.module.scss';
import {
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';
import ChannelPublishItem from './PublishChannelItem/PublishChannelItem';

const PublishView = ({ publicationData, watch, register, setValue }: IPublishViewProps): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategoryIds, setFilteredCategoryIds] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const { nestedCategories: nestedVenueCategories = [], data } = useVenueCategories();

  // Initialize all channels as collapsed
  const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>(() => {
    return publicationData.reduce(
      (acc, channel) => {
        acc[channel.channel_id] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  });

  const toggleChannel = (channelId: string) => {
    setExpandedChannels((prev) => ({
      ...prev,
      [channelId]: !prev[channelId],
    }));
  };

  const getSelectedCount = (channelId: string): number => {
    const watchChannel = watch(`channels.${channelId}`) ?? {};
    return Object.values(watchChannel).filter(Boolean).length;
  };

  const filterVenues = useMemo(
    () => (venues: IPublishVenue[]) => {
      // Precompute allowed venue IDs once before filtering
      const allowedVenueIds =
        filteredCategoryIds.length > 0
          ? new Set(venuesInSelectedCategories(filteredCategoryIds, data?.categories ?? []))
          : null;

      return venues.filter(({ venue_id, name }) => {
        // Check if venue name matches search query
        const matchesSearch = debouncedSearchTerm
          ? name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
          : true;

        // If no categories selected, show all venues that match search
        if (!allowedVenueIds) {
          return matchesSearch;
        }

        // If categories are selected, show only venues in those categories that also match search
        return allowedVenueIds.has(venue_id) && matchesSearch;
      });
    },
    [debouncedSearchTerm, filteredCategoryIds, data],
  );

  const mappedVenueCategories = useMemo(
    () => venueCategoriesToSelectOptions(nestedVenueCategories),
    [nestedVenueCategories],
  );

  return (
    <section>
      <Typography weight="semi-bold" size="xxlarge" className={styles['subheader']}>
        Publish destination
      </Typography>
      <Typography weight="semi-bold">Send menu to:</Typography>

      <div className={styles['filters-container']}>
        <SearchField isDisabled={false} placeholder="Search" onChange={(e) => setSearchTerm(e.target.value)} />
        <MultiSelect
          label="Categories"
          Icon={VenueIcon}
          options={mappedVenueCategories}
          selected={filteredCategoryIds}
          onChange={setFilteredCategoryIds}
          contentClassName={styles['filter-multiselect-content']}
        />
      </div>

      <div className={styles['send-menu-section']}>
        {publicationData.map((channel) => {
          const selectedCount = getSelectedCount(channel.channel_id);
          const isExpanded = expandedChannels[channel.channel_id] ?? true;
          const filteredVenues = filterVenues(channel.venues);

          return (
            <ChannelPublishItem
              key={channel.channel_id}
              channel={channel}
              filteredVenues={filteredVenues}
              isExpanded={isExpanded}
              selectedCount={selectedCount}
              watch={watch}
              register={register}
              setValue={setValue}
              onToggleExpand={() => toggleChannel(channel.channel_id)}
            />
          );
        })}
      </div>
    </section>
  );
};

export default PublishView;
